import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

// Wrapper component to use hooks in class component
const UserManagementWithAuth = (props) => {
  const auth = useAuth();
  return (
    <ProtectedRoute requiredRole="Admin">
      <UserManagement {...props} auth={auth} />
    </ProtectedRoute>
  );
};

class UserManagement extends Component {
  static displayName = UserManagement.name;

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      loading: true,
      error: null,
      editModal: false,
      selectedUser: null,
      editForm: {
        email: '',
        firstName: '',
        lastName: '',
        isEnabled: true,
        role: 'User'
      }
    };
  }

  async componentDidMount() {
    await this.loadUsers();
  }

  loadUsers = async () => {
    try {
      this.setState({ loading: true });
      const response = await this.props.auth.authenticatedFetch('/api/users');

      if (response && response.ok) {
        const users = await response.json();
        this.setState({ users, loading: false });
      } else if (response) {
        this.setState({ error: 'Failed to load users', loading: false });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({ error: 'Network error', loading: false });
    }
  };

  toggleUserStatus = async (userId) => {
    try {
      const response = await fetch(`/api/users/${userId}/toggle-status`, {
        method: 'POST',
        headers: {
          ...this.props.auth.getAuthHeaders()
        }
      });

      if (response.ok) {
        await this.loadUsers();
      } else {
        this.setState({ error: 'Failed to update user status' });
      }
    } catch (error) {
      this.setState({ error: 'Network error' });
    }
  };

  changeUserRole = async (userId, role) => {
    try {
      const response = await fetch(`/api/users/${userId}/change-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.props.auth.getAuthHeaders()
        },
        body: JSON.stringify(role)
      });

      if (response.ok) {
        await this.loadUsers();
      } else {
        this.setState({ error: 'Failed to update user role' });
      }
    } catch (error) {
      this.setState({ error: 'Network error' });
    }
  };

  deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          ...this.props.auth.getAuthHeaders()
        }
      });

      if (response.ok) {
        await this.loadUsers();
      } else {
        this.setState({ error: 'Failed to delete user' });
      }
    } catch (error) {
      this.setState({ error: 'Network error' });
    }
  };

  openEditModal = (user) => {
    this.setState({
      selectedUser: user,
      editForm: {
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        isEnabled: user.isEnabled,
        role: this.getRoleString(user.role) // Конвертируем число в строку для формы
      },
      editModal: true
    });
  };

  closeEditModal = () => {
    this.setState({
      editModal: false,
      selectedUser: null,
      editForm: {
        email: '',
        firstName: '',
        lastName: '',
        isEnabled: true,
        role: 'User'
      }
    });
  };

  handleEditFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    this.setState({
      editForm: {
        ...this.state.editForm,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  saveUserChanges = async () => {
    try {
      const { editForm } = this.state;

      // Конвертируем роль в число для API
      const requestData = {
        email: editForm.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        isEnabled: editForm.isEnabled,
        role: this.getRoleNumber(editForm.role)
      };

      const response = await fetch(`/api/users/${this.state.selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.props.auth.getAuthHeaders()
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        await this.loadUsers();
        this.closeEditModal();
      } else {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось обновить пользователя' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  getRoleDisplayName = (role) => {
    switch (role) {
      case 0:
      case 'User': return 'Пользователь';
      case 1:
      case 'Manager': return 'Менеджер';
      case 2:
      case 'Admin': return 'Администратор';
      default: return 'Неизвестно';
    }
  };

  getRoleBadgeColor = (role) => {
    const roleNum = typeof role === 'number' ? role : this.getRoleNumber(role);
    switch (roleNum) {
      case 2: return 'danger';   // Admin
      case 1: return 'warning';  // Manager
      case 0: return 'primary';  // User
      default: return 'secondary';
    }
  };

  getRoleNumber = (role) => {
    switch (role) {
      case 'Admin': return 2;
      case 'Manager': return 1;
      case 'User': return 0;
      default: return 0;
    }
  };

  getRoleString = (roleNumber) => {
    switch (roleNumber) {
      case 2: return 'Admin';
      case 1: return 'Manager';
      case 0: return 'User';
      default: return 'User';
    }
  };

  formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  render() {
    const { users, loading, error, editModal, editForm } = this.state;

    if (loading) {
      return (
        <Container className="mt-5">
          <div className="text-center">Loading users...</div>
        </Container>
      );
    }

    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h3 className="mb-0">User Management</h3>
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" toggle={() => this.setState({ error: null })}>
                    {error}
                  </Alert>
                )}

                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>👤 Пользователь</th>
                      <th>📧 Email</th>
                      <th>📝 Имя</th>
                      <th>🎭 Роль</th>
                      <th>🔄 Статус</th>
                      <th>📅 Создан</th>
                      <th>🕐 Последний вход</th>
                      <th>⚙️ Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                        <td>
                          <span className="badge text-dark" style={{backgroundColor: '#e9ecef'}}>
                            {this.getRoleDisplayName(user.role)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge text-dark ${user.isEnabled ? 'bg-success' : 'bg-secondary'}`}>
                            {user.isEnabled ? 'Активен' : 'Неактивен'}
                          </span>
                        </td>
                        <td>{this.formatDate(user.createdAt)}</td>
                        <td>{user.lastLoginAt ? this.formatDate(user.lastLoginAt) : 'Никогда'}</td>
                        <td>
                          <Button
                            size="sm"
                            color="primary"
                            className="me-2"
                            onClick={() => this.openEditModal(user)}
                          >
                            ✏️ Изменить
                          </Button>
                          <Button
                            size="sm"
                            color={user.isEnabled ? 'warning' : 'success'}
                            className="me-2"
                            onClick={() => this.toggleUserStatus(user.id)}
                          >
                            {user.isEnabled ? '🚫 Деактивировать' : '✅ Активировать'}
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            onClick={() => this.deleteUser(user.id)}
                          >
                            🗑️ Удалить
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {users.length === 0 && (
                  <div className="text-center text-muted">Пользователи не найдены</div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit User Modal */}
        <Modal isOpen={editModal} toggle={this.closeEditModal}>
          <ModalHeader toggle={this.closeEditModal}>
            Редактировать пользователя: {this.state.selectedUser?.username}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={editForm.email}
                  onChange={this.handleEditFormChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="firstName">Имя</Label>
                <Input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={editForm.firstName}
                  onChange={this.handleEditFormChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="lastName">Фамилия</Label>
                <Input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={editForm.lastName}
                  onChange={this.handleEditFormChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="role">Роль</Label>
                <Input
                  type="select"
                  name="role"
                  id="role"
                  value={editForm.role}
                  onChange={this.handleEditFormChange}
                >
                  <option value="User">Пользователь</option>
                  <option value="Manager">Менеджер</option>
                  <option value="Admin">Администратор</option>
                </Input>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    name="isEnabled"
                    checked={editForm.isEnabled}
                    onChange={this.handleEditFormChange}
                  />
                  Пользователь активен
                </Label>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.saveUserChanges}>
              Сохранить изменения
            </Button>
            <Button color="secondary" onClick={this.closeEditModal}>
              Отмена
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }
}

// Export the wrapper component instead of the class component
export { UserManagementWithAuth as UserManagement };
